import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

type ChatExpandTooltipProps = {
  /** Expanded label shown on hover; when empty, children render without a tooltip. */
  content?: string | null
  children: ReactNode
}

type TipCoords = { top: number; left: number }

/**
 * Portaled hover tooltip so expansions aren’t clipped by chat-bubble / panel overflow.
 * Matches Flowbite drawer tooltips: element stays mounted; open toggles
 * `invisible opacity-0` with `transition-opacity duration-300`.
 */
const ChatExpandTooltip: React.FC<ChatExpandTooltipProps> = ({ content, children }) => {
  const tip = content?.trim()
  const triggerRef = useRef<HTMLSpanElement>(null)
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState<TipCoords>({ top: 0, left: 0 })

  const updateCoords = useCallback(() => {
    const el = triggerRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    setCoords({ top: rect.top, left: rect.left + rect.width / 2 })
  }, [])

  useLayoutEffect(() => {
    if (!open) return
    updateCoords()
    const onScrollOrResize = () => updateCoords()
    window.addEventListener('scroll', onScrollOrResize, true)
    window.addEventListener('resize', onScrollOrResize)
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true)
      window.removeEventListener('resize', onScrollOrResize)
    }
  }, [open, updateCoords])

  const show = useCallback(() => {
    updateCoords()
    setOpen(true)
  }, [updateCoords])

  const hide = useCallback(() => setOpen(false), [])

  if (!tip) return <>{children}</>

  return (
    <span
      ref={triggerRef}
      className="inline-flex max-w-full items-start"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {createPortal(
        <span
          role="tooltip"
          className={`pointer-events-none fixed z-[200] inline-block w-max max-w-[min(20rem,70vw)] -translate-x-1/2 -translate-y-full whitespace-normal rounded-lg bg-slate-600 px-3 py-2 text-left text-xs font-medium text-white shadow-sm transition-opacity duration-300 dark:bg-slate-950 ${
            open ? 'opacity-100' : 'invisible opacity-0'
          }`}
          style={{ top: coords.top - 6, left: coords.left }}
        >
          <span className="relative z-20">{tip}</span>
          <span
            aria-hidden
            className="absolute left-1/2 top-full z-10 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-slate-600 dark:bg-slate-950"
          />
        </span>,
        document.body
      )}
    </span>
  )
}

export default ChatExpandTooltip
